import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export type SerializedMentionNode = Spread<
  {
    mentionId: string;
    mentionName: string;
    refType: string;
  },
  SerializedLexicalNode
>;

const MENTION_REF_REGEX = /@\{ref:user::id:(\d+)::name:([^}]+)\}/;
export class MentionNode extends DecoratorNode<JSX.Element> {
  mentionId: string;
  mentionName: string;
  refType: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.mentionId,
      node.mentionName,
      node.refType,
      node.__key
    );
  }

  constructor(
    mentionId = '',
    mentionName = '',
    refType = 'user',
    key?: NodeKey
  ) {
    super(key);
    this.mentionId = mentionId;
    this.mentionName = mentionName;
    this.refType = refType;
  }

  // called when the editor is initalized
  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'lexical-mention';
    span.setAttribute('data-type', 'mention');
    span.setAttribute('data-id', this.mentionId);
    span.setAttribute('data-name', this.mentionName);
    span.setAttribute('data-ref-type', this.refType);
    return span;
  }

  updateDOM(): false {
    return false;
  }

  /**
   * Gets called by lexical when we get the HTML value from it,
   * Replaces the user-friendly mention span with raw `@{ref:...}` text
   */
  exportDOM(): DOMExportOutput {
    const span = document.createElement('span');
    span.textContent = `@{ref:${this.refType}::id:${this.mentionId}::name:${this.mentionName}}`;
    return { element: span };
  }

  /**
   * Does the opposite, creates mention nodes from backend values
   */
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        const text = domNode.textContent || '';
        if (!MENTION_REF_REGEX.test(text)) return null;

        return {
          conversion: (element: HTMLElement): DOMConversionOutput | null => {
            const content = element.textContent || '';
            const match = MENTION_REF_REGEX.exec(content);
            if (!match) return null;

            return {
              node: $createMentionNode(match[1], match[2], 'user'),
            };
          },
          priority: 1,
        };
      },
    };
  }

  getTextContent(): string {
    return `@${this.mentionName}`;
  }

  isInline(): boolean {
    return true;
  }

  decorate(): JSX.Element {
    return (
      <span className="lexical-mention" data-mention-id={this.mentionId}>
        @{this.mentionName}
      </span>
    );
  }
}

export function $createMentionNode(
  id: string,
  name: string,
  refType = 'user'
): MentionNode {
  return new MentionNode(id, name, refType);
}

export function $isMentionNode(node: LexicalNode): node is MentionNode {
  return node instanceof MentionNode;
}
